import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import DateInput, { type DateValue } from '../../../../shared/components/DateInput';
import type { DocState, PassportIdFields } from './types';
import { isoToDateValue, dateValueToIso, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

interface Props {
  docs: DocState[];
  onDocsChange: (next: DocState[]) => void;
  handleUpload: (docId: string, filename: string) => void;
  handleReviewChange?: (docId: string, status: DocumentReviewStatus, extra?: Partial<DocState>) => void;
  passportIdFields: PassportIdFields | undefined;
  onPassportIdChange?: (key: keyof PassportIdFields, value: string) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewDocument?: (doc: DocState) => void;
}

const INPUT_CLS =
  'w-full min-h-[44px] px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300';
const LABEL_CLS = 'block text-[13px] font-semibold text-slate-900 mb-1.5';

function mapIdentityDocTypeForUpload(hasPassport: string, docType: string) {
  if (String(hasPassport || '').trim().toLowerCase() !== 'no') return 'passport_id';
  const normalized = String(docType || '').trim().toLowerCase();
  if (normalized === 'nationalid') return 'national_id_card_id';
  if (normalized === 'traveldoc') return 'travel_document_id';
  if (normalized === 'cedulainscripcion') return 'cedula_de_inscripcion_id';
  if (normalized === 'other') return 'other_id';
  return 'other_id';
}

function selectedIdentityUiType(fields: PassportIdFields) {
  return String(fields.hasPassport || '').trim().toLowerCase() === 'no'
    ? String(fields.docType || '').trim()
    : 'passport';
}

function getSelectedIdentityNumber(fields: PassportIdFields) {
  if (fields.docType === 'passport') return fields.passportNumber;
  if (fields.docType === 'nationalId') return fields.nationalIdNumber;
  if (fields.docType === 'travelDoc') return fields.travelDocumentNumber;
  if (fields.docType === 'cedulaInscripcion') return fields.registrationDocumentNumber;
  if (fields.docType === 'other') return fields.otherDocumentNumber;
  return '';
}

function getSelectedIdentityNumberKey(fields: PassportIdFields): keyof PassportIdFields {
  if (fields.docType === 'nationalId') return 'nationalIdNumber';
  if (fields.docType === 'travelDoc') return 'travelDocumentNumber';
  if (fields.docType === 'cedulaInscripcion') return 'registrationDocumentNumber';
  if (fields.docType === 'other') return 'otherDocumentNumber';
  return 'nationalIdNumber';
}

export default function IdentityCategory({
  docs,
  onDocsChange,
  passportIdFields,
  onPassportIdChange,
  categoryReview,
  onCategoryReviewChange,
  onViewDocument,
}: Props) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  if (!passportIdFields) return null;

  const hasPassport = passportIdFields.hasPassport || 'yes';
  const noPassport = hasPassport === 'no';
  const [dobDraft, setDobDraft] = useState<DateValue>(() => isoToDateValue(passportIdFields.dob));

  const isCompleteDate = (v: DateValue) =>
    Boolean(v?.day) && Boolean(v?.month) && String(v?.year || '').length === 4;

  const isEmptyDate = (v: DateValue) =>
    !String(v?.day || '').trim() && !String(v?.month || '').trim() && !String(v?.year || '').trim();

  useEffect(() => {
    setDobDraft(isoToDateValue(passportIdFields.dob));
  }, [passportIdFields.dob]);

  /* ── Fields view ── */
  const docLabel = noPassport
    ? t('advisorPortal.identityDocTypeLabel', { defaultValue: 'Identity document' })
    : t('advisorPortal.identityPassportLabel', { defaultValue: 'Passport' });

  return (
    <div className="max-w-[720px]">
      <CategoryHeader
        title={t('advisorPortal.identityDocumentsTitle', { defaultValue: 'Identity documents' })}
        category="passport"
        status={categoryReview}
        onChange={onCategoryReviewChange}
      />
      {/* Card selector */}
      <div className="mb-5 grid grid-cols-1 gap-2 max-w-[420px] min-[420px]:grid-cols-2">
        <button
          type="button"
          onClick={() => onPassportIdChange?.('hasPassport', 'yes')}
          className={`min-h-[44px] rounded-md border text-sm font-semibold transition-all cursor-pointer ${
            !noPassport
              ? 'bg-emerald-50 border-emerald-400 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          {t('advisorPortal.identityPassportLabel', { defaultValue: 'Passport' })}
        </button>
        <button
          type="button"
          onClick={() => onPassportIdChange?.('hasPassport', 'no')}
          className={`min-h-[44px] rounded-md border text-sm font-semibold transition-all cursor-pointer ${
            noPassport
              ? 'bg-emerald-50 border-emerald-400 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
          }`}
        >
          {t('advisorPortal.identityOtherIdLabel', { defaultValue: 'Other ID' })}
        </button>
      </div>

      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
        {t('advisorPortal.identityStep1', { defaultValue: 'Step 1 — Enter details' })}
      </p>

      {/* Fields card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-8">
        <div className="flex flex-col gap-4">
          {noPassport ? (
            <>
              {/* Other ID: Document type */}
              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.identityDocumentType', { defaultValue: 'Document type' })}</label>
                <select
                  value={passportIdFields.docType}
                  onChange={(e) => onPassportIdChange?.('docType', e.target.value)}
                  className={`${INPUT_CLS} ${!passportIdFields.docType ? 'text-slate-400' : 'text-slate-900'}`}
                >
                  <option value="">{t('advisorPortal.identitySelect', { defaultValue: 'Select...' })}</option>
                  <option value="nationalId">{t('advisorPortal.identityNationalId', { defaultValue: 'National ID' })}</option>
                  <option value="travelDoc">{t('advisorPortal.identityTravelDocument', { defaultValue: 'Travel Document' })}</option>
                  <option value="cedulaInscripcion">{t('advisorPortal.identityCedula', { defaultValue: 'Cédula de Inscripción' })}</option>
                  <option value="other">{t('advisorPortal.docCat_other_name')}</option>
                </select>
              </div>

              {/* Other ID: ID number */}
              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.identityIdNumber', { defaultValue: 'ID number' })}</label>
                <input
                  type="text"
                  value={getSelectedIdentityNumber(passportIdFields)}
                  onChange={(e) => onPassportIdChange?.(getSelectedIdentityNumberKey(passportIdFields), e.target.value)}
                  placeholder=""
                  className={INPUT_CLS}
                />
              </div>

              {/* Other ID: Date of birth */}
              <DateInput
                label={t('advisorPortal.fieldDateOfBirth')}
                value={dobDraft}
                onChange={(v) => {
                  setDobDraft(v);
                  // Avoid erasing the field while the user is mid-edit (partial date).
                  // Persist only when complete, or when fully cleared.
                  if (isCompleteDate(v) || isEmptyDate(v)) {
                    onPassportIdChange?.('dob', dateValueToIso(v));
                  }
                }}
              />

              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldSex')}</label>
                <select
                  value={passportIdFields.sex || ''}
                  onChange={(e) => onPassportIdChange?.('sex', e.target.value)}
                  className={`${INPUT_CLS} ${!passportIdFields.sex ? 'text-slate-400' : 'text-slate-900'}`}
                >
                  <option value="">{t('advisorPortal.identitySelect', { defaultValue: 'Select...' })}</option>
                  <option value="male">{t('advisorPortal.sexMale', { defaultValue: 'Male' })}</option>
                  <option value="female">{t('advisorPortal.sexFemale', { defaultValue: 'Female' })}</option>
                  <option value="undetermined">{t('advisorPortal.sexUndetermined', { defaultValue: 'Undetermined' })}</option>
                </select>
              </div>

              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldCountryOfBirth')}</label>
                <input
                  type="text"
                  value={passportIdFields.countryOfBirth || ''}
                  onChange={(e) => onPassportIdChange?.('countryOfBirth', e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldPlaceOfBirth')}</label>
                <input
                  type="text"
                  value={passportIdFields.placeOfBirth || ''}
                  onChange={(e) => onPassportIdChange?.('placeOfBirth', e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

            </>
          ) : (
            <>
              {/* Passport: Passport number */}
              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldPassportNumber')}</label>
                <input
                  type="text"
                  value={passportIdFields.passportNumber}
                  onChange={(e) =>
                    onPassportIdChange?.(
                      'passportNumber',
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                    )
                  }
                  placeholder="AB1234567"
                  className={INPUT_CLS}
                />
              </div>

              {/* Passport: Full name */}
              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.identityFullNamePassport', { defaultValue: 'Full name (as on passport)' })}</label>
                <input
                  type="text"
                  value={passportIdFields.docFullName}
                  onChange={(e) =>
                    onPassportIdChange?.('docFullName', e.target.value.toUpperCase())
                  }
                  placeholder="GARCÍA LÓPEZ, MARÍA"
                  className={INPUT_CLS}
                />
              </div>

              {/* Passport: Date of birth */}
              <DateInput
                label={t('advisorPortal.fieldDateOfBirth')}
                value={dobDraft}
                onChange={(v) => {
                  setDobDraft(v);
                  if (isCompleteDate(v) || isEmptyDate(v)) {
                    onPassportIdChange?.('dob', dateValueToIso(v));
                  }
                }}
              />

              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldSex')}</label>
                <select
                  value={passportIdFields.sex || ''}
                  onChange={(e) => onPassportIdChange?.('sex', e.target.value)}
                  className={`${INPUT_CLS} ${!passportIdFields.sex ? 'text-slate-400' : 'text-slate-900'}`}
                >
                  <option value="">{t('advisorPortal.identitySelect', { defaultValue: 'Select...' })}</option>
                  <option value="male">{t('advisorPortal.sexMale', { defaultValue: 'Male' })}</option>
                  <option value="female">{t('advisorPortal.sexFemale', { defaultValue: 'Female' })}</option>
                  <option value="undetermined">{t('advisorPortal.sexUndetermined', { defaultValue: 'Undetermined' })}</option>
                </select>
              </div>

              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldCountryOfBirth')}</label>
                <input
                  type="text"
                  value={passportIdFields.countryOfBirth || ''}
                  onChange={(e) => onPassportIdChange?.('countryOfBirth', e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

              <div>
                <label className={LABEL_CLS}>{t('advisorPortal.fieldPlaceOfBirth')}</label>
                <input
                  type="text"
                  value={passportIdFields.placeOfBirth || ''}
                  onChange={(e) => onPassportIdChange?.('placeOfBirth', e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

            </>
          )}
        </div>
      </div>

      {/* Step 2 */}
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
        {t('advisorPortal.identityStep2', { defaultValue: 'Step 2 — Upload the document' })}
      </p>
      {(() => {
        const selectedUploadType = mapIdentityDocTypeForUpload(
          passportIdFields.hasPassport,
          passportIdFields.docType,
        );
        const selectedDocType = selectedIdentityUiType(passportIdFields);
        const existing = docs.find(
          (d) =>
            d.category === 'passport' &&
            (String(d.docType || '').trim() === selectedDocType ||
              String(d.docType || '').trim().toLowerCase() === selectedUploadType),
        );
        const uploadedFile = existing?.uploaded ? (existing.uploadedFile ?? '') : '';

        const handleFile = (file: File) => {
          if (existing) {
            onDocsChange(
              docs.map((d) =>
                d.id === existing.id
                  ? {
                      ...d,
                      docType: selectedDocType,
                      uploaded: true,
                      uploadedAt: new Date().toISOString().slice(0, 10),
                      uploadedFile: file.name,
                      rawFile: file,
                      reviewStatus: 'pending' as DocumentReviewStatus,
                    }
                  : d,
              ),
            );
          } else {
            onDocsChange([
              ...docs,
              {
                id: `pas-${Date.now()}`,
                name: docLabel,
                category: 'passport',
                docType: selectedDocType,
                uploaded: true,
                uploadedAt: new Date().toISOString().slice(0, 10),
                uploadedFile: file.name,
                rawFile: file,
                reviewStatus: 'pending' as DocumentReviewStatus,
              },
            ]);
          }
        };

        if (uploadedFile) {
          return (
            <div className="flex flex-col gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 max-w-[480px] min-[420px]:flex-row min-[420px]:items-center">
              <i className="ri-file-check-line text-emerald-600 text-sm shrink-0" />
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <p className="min-w-0 text-[12px] font-medium text-emerald-800 truncate">{uploadedFile}</p>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(existing?.reviewStatus ?? 'pending')}`}>
                  {reviewStatusLabel(existing?.reviewStatus ?? 'pending')}
                </span>
              </div>
              <button
                type="button"
                onClick={() => existing && onViewDocument?.(existing)}
                className="min-h-[36px] text-[11px] text-slate-600 font-medium border border-slate-200 rounded-md px-2 py-1 cursor-pointer hover:bg-slate-50 shrink-0 whitespace-nowrap bg-white"
              >
                {t('advisorPortal.docPreview', { defaultValue: 'View' })}
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="min-h-[36px] text-[11px] text-emerald-700 font-medium underline underline-offset-2 cursor-pointer hover:text-emerald-800 shrink-0 whitespace-nowrap"
              >
                {t('advisorPortal.changeFile', { defaultValue: 'Change' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (existing) {
                    onDocsChange(
                      docs.map((d) =>
                        d.id === existing.id
                          ? { ...d, uploaded: false, uploadedFile: undefined, uploadedAt: undefined }
                          : d,
                      ),
                    );
                  }
                }}
                className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer shrink-0"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={DOCUMENT_FILE_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = '';
                }}
              />
            </div>
          );
        }

        return (
          <div className="max-w-[480px]">
            <div className="flex">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full min-h-[44px] bg-white border border-slate-200 rounded-lg flex items-center justify-center gap-2 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 cursor-pointer"
              >
                <i className="ri-file-upload-line" /> {t('docUpload.chooseFile', { defaultValue: 'Choose file' })}
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={DOCUMENT_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.target.value = '';
              }}
            />
          </div>
        );
      })()}
    </div>
  );
}
